// Dev-message banner: top-centre DOM overlay above the canvas, visible on
// every scene. FIFO — one message at a time, BANNER_MS each, then gone
// forever (no history). Pure queue split from the DOM so it unit-tests.
import './messageBanner.css';

export const BANNER_MS = 10_000;

export interface BannerSink {
  show(sender: string, text: string, senderRole?: string): void;
  hide(): void;
}

export class BannerQueue {
  private queue: { sender: string; text: string; senderRole?: string }[] = [];
  private showing = false;

  constructor(
    private readonly sink: BannerSink,
    private readonly ms: number = BANNER_MS,
  ) {}

  push(sender: string, text: string, senderRole?: string): void {
    this.queue.push({ sender, text, senderRole });
    if (!this.showing) this.next();
  }

  private next(): void {
    const item = this.queue.shift();
    if (!item) {
      this.showing = false;
      return;
    }
    this.showing = true;
    this.sink.show(item.sender, item.text, item.senderRole);
    setTimeout(() => {
      this.sink.hide();
      this.next();
    }, this.ms);
  }
}

// ── DOM singleton ───────────────────────────────────────────────────────────
let domQueue: BannerQueue | null = null;
let root: HTMLDivElement | null = null;

function domSink(): BannerSink {
  return {
    show(sender, text, senderRole) {
      root = document.createElement('div');
      // Higher-dev senders get a blue identity; everyone else the default gold.
      root.className = senderRole === 'higher_dev' ? 'dev-banner dev-banner-higher' : 'dev-banner';
      root.dataset.testid = 'dev-banner';
      const s = document.createElement('span');
      s.className = 'dev-banner-sender';
      s.dataset.testid = 'dev-banner-sender';
      s.textContent = sender;
      const t = document.createElement('span');
      t.className = 'dev-banner-text';
      t.dataset.testid = 'dev-banner-text';
      t.textContent = text;
      root.append(s, ': ', t);
      document.body.append(root);
    },
    hide() {
      root?.remove();
      root = null;
    },
  };
}

/** Queue a dev message banner (creates the overlay lazily on first use). */
export function enqueueBanner(sender: string, text: string, senderRole?: string): void {
  domQueue ??= new BannerQueue(domSink());
  domQueue.push(sender, text, senderRole);
}
